/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState } from 'react';
import {
    string, object, bool,
} from 'prop-types';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import { useMediaQuery } from '@material-ui/core';
import PDPDeliveryPopup from './PDPDeliveryPopup';
import { getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';

const useStyles = makeStyles((theme) => ({
    title: {
        textAlign: 'center',
        margin: 0,
        padding: '0 8px',
        textTransform: 'Uppercase',
    },
    popupButton: (colors) => ({
        backgroundColor: colors?.button_background_color || '#FFFFFF',
        border: colors?.button_border_color || 'none',
        boxShadow: colors?.button_shadow || '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
        fontSize: '12px',
        fontWeight: 'bold',
        width: '100%',
        padding: '8px',
        whiteSpace: 'nowrap',
        '&:hover': {
            backgroundColor: 'inherit',
        },
        [theme.breakpoints.down('sm')]: {
            whiteSpace: 'break-spaces',
        },
    }),
    additionalText: (colors) => ({
        color: colors?.button_additional_text_color || 'inherit',
        '&.orderDetails': {
            color: '#4A90E2',
            fontSize: '11px !important',
            textDecoration: 'underline',
        },
    }),
    deliveryDetail: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '4px 8px',
        borderRadius: '3px',
        '&.orderDetails': {
            flexDirection: 'column',
        },

        '&.orderDetailsMargin': {
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: '1.2em',
        },
    },
    deliveryDetailsIcon: {
        display: 'block',
        width: '32px',
    },
    titleCheckout: {
        textAlign: 'left',
        margin: 0,
        padding: 7,
    },
    titleOrderDetails: {
        textAlign: 'center',
        textTransform: 'capitalize',
        textDecoration: 'underline',
        marginBottom: '0 !important',
    },
    popupButtonCheckout: {
        fontSize: '12px',
        fontWeight: 'bold',
        width: '100%',
        padding: '10px 0',
        whiteSpace: 'nowrap',
        '&:hover': {
            backgroundColor: 'inherit',
        },
        [theme.breakpoints.down('sm')]: {
            whiteSpace: 'break-spaces',
        },
    },
    popupButtonOrderDetails: {
        fontSize: '12px',
        fontWeight: 'bold',
        width: '100%',
        backgroundColor: 'transparent !important',
        border: 'none !important',
        minWidth: '50px !important',
        marginRight: '0 !important',
        position: 'absolute',
        top: 'calc(-50% + 10px)',
        right: 0,
        padding: 0,
        whiteSpace: 'nowrap',
        '&:hover': {
            backgroundColor: 'inherit',
        },
        [theme.breakpoints.down('sm')]: {
            whiteSpace: 'break-spaces',
        },
    },
    newDesignButton: {
        padding: '8px 16px 12px',
        justifyContent: 'flex-start',
        '& $deliveryDetail': {
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
        },
        '& $svgIcon': {
            marginTop: '2px',
        },
        '& $title': {
            fontSize: '16px',
            lineHeight: '24px',
            textTransform: 'initial',
            textAlign: 'left',
            padding: '0px 12px',
        },
        [theme.breakpoints.up('600')]: {
            padding: '0px 0px 12px 16px',
            '& $title': {
                padding: '0px 0px 0px 12px',
            },
        },
        '&:hover': {
            background: 'transparent',
        },
        '& .MuiTouchRipple-child': {
            background: 'transparent',
        },
        '& .MuiButtonBase-root:hover': {
            backgroundColor: 'transparent',
        },
    },
    svgIcon: {
        height: '20px',
        [theme.breakpoints.down('600')]: {
            '& svg': {
                width: '20px',
            },
        },
    },
    popTitleTop: {
        [theme.breakpoints.down('600')]: {
            padding: '8px 16px 0px',
        },
    },
    plaRedesignDeliveryButton: {
        padding: '8px 0px 0px 24px',
        '@media screen and (max-width:600px)': {
            padding: '8px 0px 0px 16px',
        },
    },
}));

const PDPDeliveryButton = ({
    data, pageType, isROSSPassport, isPdpRedesignEnabled, productDeliveryType, showProductTitleTop, isPlaRedesignEnabled, brand, productBrand,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const isDeliveryTypeFlag = useSelector(getFeatureFlag('is-delivery-type-pdp-page-data-enabled'));
    const gptDeliveryTypeData = isDeliveryTypeFlag?.[brand?.domain]?.GPT || {};
    const productKey = String(productBrand);
    function toggle() {
        setIsModalOpen(!isModalOpen);
    }

    const buttonAttributes = data?.findContent?.content?.entries[0]?.button_attributes;
    const pdpRedesignButtonAttribute = data?.findContent?.content?.entries[0]?.delivery_type;
    const popUpData = data?.findContent?.content?.entries || [];

    const classes = useStyles(buttonAttributes?.colors);

    let buttonStyle = classes.popupButton;
    if (pageType === 'checkout') {
        buttonStyle = classes.popupButtonCheckout;
    } else if (pageType === 'order-details') {
        buttonStyle = classes.popupButtonOrderDetails;
    } else if (isPdpRedesignEnabled && pageType !== 'checkout' && pageType !== 'order-details') {
        buttonStyle = `${classes.newDesignButton} ${showProductTitleTop ? classes.popTitleTop : ''}`;
    }

    let deliveryDetailStyle = classes.deliveryDetail;
    if (pageType === 'order-details') {
        deliveryDetailStyle = isROSSPassport ? `${classes.deliveryDetail} orderDetailsMargin` : `${classes.deliveryDetail} orderDetails`;
    }

    let titleStyle = classes.title;
    if (pageType === 'checkout') {
        titleStyle = classes.titleCheckout;
    } else if (pageType === 'order-details') {
        titleStyle = classes.titleOrderDetails;
    }

    let buttonText = buttonAttributes?.content?.button_text ? buttonAttributes.content.button_text.toLowerCase() : '';
    if (pageType === 'order-details') {
        buttonText = '';
    }

    const pdpRedesignButtonText = pdpRedesignButtonAttribute?.text ? pdpRedesignButtonAttribute?.text?.toLowerCase() : '';
    const cleanAndCapitalize = (text) => {
        const cleanedText = text?.replace('-', ''); // Remove the hyphen
        const firstWord = text?.split(' ')[0];  // Get the first word (designed)
        const capitalized = firstWord?.charAt?.(0)?.toUpperCase() + firstWord?.slice?.(1);  // Capitalize first letter
        return text?.replace(firstWord, capitalized);  // Replace with the capitalized word
    };

    let imageUrl = '';
    const updateImageUrl = () => {
        if (productDeliveryType?.toLowerCase() === 'gpt' && gptDeliveryTypeData?.[productKey]) {
            imageUrl =  gptDeliveryTypeData?.image;
        } else if (isPdpRedesignEnabled && pdpRedesignButtonAttribute?.icon?.url) {
            imageUrl = pdpRedesignButtonAttribute?.icon?.url;
        } else if (buttonAttributes?.content?.button_image?.url) {
            imageUrl = buttonAttributes?.content?.button_image?.url;
        }
        return imageUrl;
    };

    let deliveryText = '';
    const updateDeliveryText = () => {
        if (productDeliveryType?.toLowerCase() === 'gpt' && gptDeliveryTypeData?.[productKey]) {
            deliveryText = gptDeliveryTypeData?.[productKey];
        } else if (pdpRedesignButtonText && isPdpRedesignEnabled) {
            deliveryText = cleanAndCapitalize(pdpRedesignButtonText);
        } else if (buttonText) {
            deliveryText = buttonText;
        }
        return deliveryText;
    };
    return (
        <>
            {(buttonAttributes?.content?.button_text || pdpRedesignButtonText) && (
                <>
                    <Button
                        data-testid="pdp-delivery-details"
                        className={`${buttonStyle} ${isPlaRedesignEnabled ? classes.plaRedesignDeliveryButton : ''}`}
                        onClick={() => {
                            setIsModalOpen(!isModalOpen);
                        }}
                        onKeyDown={() => {
                            setIsModalOpen(!isModalOpen);
                        }}
                    >
                        <div className={deliveryDetailStyle} style={{ backgroundColor: isDeliveryTypeFlag?.[brand?.domain]?.backgroundColor, color: isDeliveryTypeFlag?.[brand?.domain]?.color }}>
                            <img className={classes.svgIcon} src={updateImageUrl()} alt="Design and Delivery Product Details" />
                            <p className={titleStyle}>
                                {updateDeliveryText()}
                                {buttonAttributes.content.button_additional_text && !isPdpRedesignEnabled && (
                                    <span className={`${classes.additionalText} ${(pageType === 'order-details') ? ' orderDetails' : ''}`}>
                                        {` ${buttonAttributes.content.button_additional_text}`}
                                    </span>
                                )}
                            </p>
                        </div>
                    </Button>
                    <PDPDeliveryPopup popUpData={popUpData} setIsModalOpen={toggle} isModalOpen={isModalOpen} />
                </>
            )}
        </>
    );
};

PDPDeliveryButton.propTypes = {
    data: object.isRequired,
    pageType: string,
    isROSSPassport: bool,
    isPdpRedesignEnabled: bool,
    productDeliveryType: string.isRequired,
    showProductTitleTop: bool,
    isPlaRedesignEnabled: bool,
    brand: object,
    productBrand: string,
};

PDPDeliveryButton.defaultProps = {
    pageType: '',
    isROSSPassport: false,
    isPdpRedesignEnabled: false,
    showProductTitleTop: false,
    isPlaRedesignEnabled: false,
    brand: {},
    productBrand: '',
};

export default PDPDeliveryButton;
